import React, { useState, useEffect, useRef } from "react";
import FormContainer from "../../../components/CommonComponents/FormContainer";
import LeftWrapper from "../../../components/CommonComponents/LeftWrapper";
import RightWrapper from "../../../components/CommonComponents/RightWrapper";
import Wrapper from "../../../components/CommonComponents/Wrapper";
import MessageHeading from "../../../components/CommonComponents/MessageHeading";
import MessageLabel from "../../../components/CommonComponents/MessageLabel";
import AdminDownloadFile from "../../../components/CommonComponents/AdminDownloadFile";
import { BsFiletypeXlsx } from "react-icons/bs";
import { toast } from "react-toastify";
import AdminSaveHeader from "../../../components/CommonComponents/AdminSaveHeader";
import { useMyContext } from "../../../components/Provider/CommonProvider";
import Defaultloadinggif from "../../../layout/Others/Defaultloadinggif";
import AdminImageUploadZone from "../../../components/CommonComponents/AdminImageUploadZone";

function ImportUser() {
  const { fetchRequest } = useMyContext();

  const [labelDisplay, setLabelDisplay] = useState([]);
  const [withOutRefresh, setWithOutRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState("");
  const [defaultOption, setDefaultOption] = useState({
    state: "defaultOption",
  });
  const [formDataError, setFormDataError] = useState("");
  const combinedRef = useRef({});

  // UI States for Progress Bar & Results
  const [importState, setImportState] = useState("idle"); // 'idle' | 'importing' | 'completed'
  const [progress, setProgress] = useState(0);
  const [resultData, setResultData] = useState(null);
  const progressInterval = useRef(null);

  const baseURL = process.env.REACT_APP_Baseurl;
  const storeApi = `${baseURL}/admin/users/bulk-import`;
  const createApi = `${baseURL}/admin/users/import-sample-file`;
  const labelApi = `${baseURL}/admin/Label-Management/list-source`;

  const handleFileChange = async (event) => {
    const { files } = event.target;
    const selectedFile = files[0];
    if (selectedFile) {
      const fileExtension = selectedFile?.name?.split(".").pop().toLowerCase();
      if (fileExtension !== "xlsx") {
        setFormDataError("Please upload a valid .xlsx file.");
        setFormData(null);
      } else {
        setFormData(selectedFile);
        setFormDataError("");
      }
    }
  };

  const handleClearSelectFile = () => {
    setFormData(null);
  };

  const handleStore = async () => {
    if (formDataError?.length) {
      combinedRef.current["file"].focus();
      return;
    }
    if (!formData) {
      combinedRef.current["file"].focus();
      setFormDataError("Please provide xlsx file.");
      return;
    }

    // Start progress simulation
    setImportState("importing");
    setProgress(5);

    progressInterval.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) {
          return 95;
        }
        const increment = Math.floor(Math.random() * 10) + 5;
        return Math.min(prev + increment, 95);
      });
    }, 450);

    const formDataFile = new FormData();
    formDataFile.append("file", formData);

    await fetchRequest({
      method: "POST",
      url: `${storeApi}`,
      params: {},
      data: formDataFile,
    })
      .then((response) => {
        clearInterval(progressInterval.current);
        if (response?.status) {
          setProgress(100);
          setResultData(response);
          setImportState("completed");
          toast.success(response?.message || "Bulk Excel Imported Successfully!");
        } else {
          setImportState("idle");
          toast.error(response?.message || "Import failed");
        }
      })
      .catch((error) => {
        clearInterval(progressInterval.current);
        setImportState("idle");
        toast.error(error?.response?.data?.message || "Failed to import users.");
      });
  };

  const handleReset = () => {
    setFormData(null);
    setFormDataError("");
    setResultData(null);
    setProgress(0);
    setImportState("idle");
    setWithOutRefresh((prev) => !prev);
  };

  useEffect(async () => {
    setLoading(true);
    await fetchRequest({
      method: "GET",
      url: `${createApi}`,
      params: {},
      data: {},
    })
      .then((response) => {
        if (response?.status) {
          setDefaultOption((prev) => ({
            ...prev,
            sampleFile: response.Sample_file_path,
          }));
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || "Something went wrong sample file");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [withOutRefresh]);

  useEffect(async () => {
    setLoading(true);
    await fetchRequest({
      method: "GET",
      url: `${labelApi}`,
      params: { source: "add-users" },
      data: {},
    })
      .then((response) => {
        setLabelDisplay(response?.label_management);
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || "Something went wrong label api");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <Defaultloadinggif className={"heightOnLoader"} />;
  }

  return (
    <>
      <AdminSaveHeader
        heading={labelDisplay?.[14]?.title}
        saveText={labelDisplay?.[19]?.title}
        saveMethod={handleStore}
        loading={importState === "importing"}
        hideButton={importState !== "idle"}
      />
      <FormContainer>
        <LeftWrapper>
          <Wrapper>
            <MessageHeading text={labelDisplay?.[15]?.title} />

            {/* IDLE state: Show file upload zone */}
            {importState === "idle" && (
              <>
                <MessageLabel text={labelDisplay?.[16]?.title} className="mandatorySimple" />
                <AdminImageUploadZone
                  onChange={handleFileChange}
                  selectedFile={formData}
                  onDelete={handleClearSelectFile}
                  ref={(el) => (combinedRef.current.file = el)}
                  name="file"
                  error={formDataError}
                  accept={".xlsx"}
                  icon={<BsFiletypeXlsx />}
                />
              </>
            )}

            {/* IMPORTING state: Show active progress bar */}
            {importState === "importing" && (
              <div className="p-4 border rounded text-center theme-bg-color-secondary theme-border-color">
                <h6 className="mb-3 fw-bold theme-text-color">Importing Users...</h6>
                <div className="progress mb-3" style={{ height: "20px" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated theme-button-bg-color"
                    role="progressbar"
                    style={{ width: `${progress}%` }}
                    aria-valuenow={progress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    {progress}%
                  </div>
                </div>
                <small className="text-muted">Please wait while we validate and import users...</small>
              </div>
            )}

            {/* COMPLETED state: Show detailed results, rates, and skipped emails */}
            {importState === "completed" && resultData && (() => {
              const successCount = resultData.imported_user_count || 0;
              const failCount = resultData.existing_emails?.length || 0;
              const total = successCount + failCount;
              const successRate = total > 0 ? ((successCount / total) * 100).toFixed(1) : 0;
              const failRate = total > 0 ? ((failCount / total) * 100).toFixed(1) : 0;

              return (
                <div className="p-4 border rounded theme-bg-color theme-border-color shadow-sm">
                  <h6 className="mb-3 fw-bold theme-text-color text-center">Import Summary</h6>

                  {/* Stacked Progress Bar */}
                  <div className="progress mb-4" style={{ height: "24px", borderRadius: "12px", overflow: "hidden" }}>
                    {successCount > 0 && (
                      <div
                        className="progress-bar greenColorBg font-weight-bold"
                        role="progressbar"
                        style={{ width: `${successRate}%` }}
                        aria-valuenow={successRate}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {successRate}% Success
                      </div>
                    )}
                    {failCount > 0 && (
                      <div
                        className="progress-bar warningColorBg font-weight-bold"
                        role="progressbar"
                        style={{ width: `${failRate}%` }}
                        aria-valuenow={failRate}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {failRate}% Skipped
                      </div>
                    )}
                  </div>

                  <div className="row text-center mb-4">
                    <div className="col-6">
                      <div className="p-3 border rounded theme-bg-color-secondary theme-border-color">
                        <h4 className="fw-bold mb-1" style={{ color: "var(--green-color)" }}>{successCount}</h4>
                        <span className="text-muted small d-block">Imported Successfully</span>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="p-3 border rounded theme-bg-color-secondary theme-border-color">
                        <h4 className="warningColor fw-bold mb-1">{failCount}</h4>
                        <span className="text-muted small d-block">Skipped (Exists)</span>
                      </div>
                    </div>
                  </div>

                  {failCount > 0 && (
                    <div className="mb-3">
                      <span className="theme-text-color fw-bold small mb-2 d-block">Skipped Emails (Already Exists):</span>
                      <div className="border rounded p-3 theme-bg-color-secondary theme-border-color" style={{ maxHeight: "130px", overflowY: "auto" }}>
                        {resultData.existing_emails.map((email, idx) => (
                          <div key={idx} className="warningColor small mb-1">
                            • {email}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    className="btn btn-sm w-100 text-color-button"
                    style={{ border: "1px solid", borderRadius: "4px" }}
                    onClick={handleReset}
                  >
                    Import Another File
                  </button>
                </div>
              );
            })()}
          </Wrapper>
        </LeftWrapper>
        <RightWrapper>
          <Wrapper>
            <MessageHeading text={labelDisplay?.[17]?.title} />
            <MessageLabel text={labelDisplay?.[18]?.title} />
            <AdminDownloadFile
              name="Download"
              link={defaultOption?.sampleFile}
              icon={<BsFiletypeXlsx className="dark-green" />}
              value="yes"
            />
          </Wrapper>
        </RightWrapper>
      </FormContainer>
    </>
  );
}

export default ImportUser;